import React, { useState, useEffect } from 'react';
import './SystemManager.css';
import { endpointAPI, adminSettingsAPI } from '../../../../services';
import { getAllEndpoints } from '../../../../services/endpoints/endpointStore';
import { adminHasPermission } from '../../../../services/auth/authManager';

const SystemManager = () => {
  // State for main tabs
  const [activeTab, setActiveTab] = useState('dns');
  
  // State for data
  const [coreDnsEntries, setCoreDnsEntries] = useState([]);
  
  // State for loading and error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for dialogs
  const [showAddDnsDialog, setShowAddDnsDialog] = useState(false);
  const [showEditDnsDialog, setShowEditDnsDialog] = useState(false);
  
  // State for current edit items
  const [currentDnsEntry, setCurrentDnsEntry] = useState(null);
  
  // State for maintenance mode
  const [maintenanceMode, setMaintenanceMode] = useState({
    isEnabled: false,
    message: '',
    supportEmail: 'support@wdtp.io',
    allowedRoutes: [],
    startedAt: null,
    endedAt: null,
    loading: false
  });
  
  // State for editing maintenance settings
  const [editingMaintenance, setEditingMaintenance] = useState({
    message: '',
    supportEmail: 'support@wdtp.io'
  });
  
  // Update editing state when maintenance mode loads
  useEffect(() => {
    if (maintenanceMode) {
      setEditingMaintenance({
        message: maintenanceMode.message || '',
        supportEmail: maintenanceMode.supportEmail || 'support@wdtp.io'
      });
    }
  }, [maintenanceMode.message, maintenanceMode.supportEmail]);
  
  // Fetch all data on component mount
  useEffect(() => {
    fetchDnsEntries();
    fetchMaintenanceStatus();
  }, []);
  
  // Function to fetch DNS entries
  const fetchDnsEntries = async () => {
    try {
      setLoading(true);
      setCoreDnsEntries([]);

      // Try to use cached endpoints first (fetched during login)
      const cachedEndpoints = getAllEndpoints();

      if (cachedEndpoints && cachedEndpoints.length > 0) {
        console.log('[SystemManager] Using cached endpoints:', cachedEndpoints.length, 'entries');
        const formattedDnsEntries = cachedEndpoints.map(entry => ({
          id: entry._id,
          name: entry.name || 'Unnamed Service',
          baseUrl: entry.baseUrl || entry.url || '',
          url: entry.baseUrl || entry.url || '',
          status: entry.isActive ? 'active' : 'inactive',
          createdAt: entry.createdAt
        }));

        setCoreDnsEntries(formattedDnsEntries);
        setLoading(false);
        return;
      }

      // Fallback: fetch from API only if no cache
      console.log('[SystemManager] No cached endpoints, fetching from API...');
      const response = await endpointAPI.getAll();

      if (response && response.data) {
        const dnsData = typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

        if (Array.isArray(dnsData)) {
          const formattedDnsEntries = dnsData.map(entry => ({
            id: entry._id,
            name: entry.name || 'Unnamed Service',
            baseUrl: entry.baseUrl || entry.url || '',
            url: entry.baseUrl || entry.url || '',
            status: entry.isActive ? 'active' : 'inactive',
            createdAt: entry.createdAt
          }));

          setCoreDnsEntries(formattedDnsEntries);
        } else {
          setCoreDnsEntries([]);
          setError('Invalid DNS data format: expected an array');
        }
      } else {
        setCoreDnsEntries([]);
        setError('Invalid response format when fetching DNS entries');
      }
    } catch (err) {
      setError('Failed to fetch DNS entries: ' + (err.message || 'Unknown error'));
      console.error('Error fetching DNS entries:', err);
      setCoreDnsEntries([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Dialog handlers
  const handleOpenAddDnsDialog = () => {
    setShowAddDnsDialog(true);
  };
  
  const handleCloseAddDnsDialog = () => {
    setShowAddDnsDialog(false);
  };
  
  const handleOpenEditDnsDialog = (dnsEntry) => {
    setCurrentDnsEntry(dnsEntry);
    setShowEditDnsDialog(true);
  };
  
  const handleCloseEditDnsDialog = () => {
    setShowEditDnsDialog(false);
    setCurrentDnsEntry(null);
  };
  
  // Add/Edit handlers
  const handleAddDnsEntry = async (formData) => {
    try {
      const response = await endpointAPI.create({
        baseUrl: formData.baseUrl.trim(),
        name: formData.name.trim()
      });
      
      if (response && (response.status === 200 || response.status === 201)) {
        console.log('DNS entry added successfully');
        setError(null);
      } else {
        throw new Error('Failed to add DNS entry');
      }
      
      await fetchDnsEntries();
      handleCloseAddDnsDialog();
    } catch (err) {
      setError('Failed to add DNS entry: ' + (err.message || 'Unknown error'));
      console.error('Error adding DNS entry:', err);
    }
  };
  
  const handleEditDnsEntry = async (formData) => {
    try {
      if (currentDnsEntry && currentDnsEntry.id) {
        const response = await endpointAPI.update(currentDnsEntry.id, {
          baseUrl: formData.baseUrl.trim(),
          name: formData.name.trim()
        });
        
        if (response && (response.status === 200 || response.status === 201)) {
          console.log('DNS entry updated successfully');
          setError(null);
        } else {
          throw new Error('Failed to update DNS entry');
        }
      } else {
        throw new Error('Current DNS entry ID not found');
      }
      
      await fetchDnsEntries();
      handleCloseEditDnsDialog();
    } catch (err) {
      setError('Failed to update DNS entry: ' + (err.message || 'Unknown error'));
      console.error('Error updating DNS entry:', err);
    }
  };
  
  const handleDeleteDnsEntry = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this DNS entry?')) {
      try {
        const response = await endpointAPI.delete(entryId);
        
        if (response && response.status === 200) {
          console.log('DNS entry deleted successfully');
        } else {
          throw new Error('Failed to delete DNS entry');
        }
        
        await fetchDnsEntries();
      } catch (err) {
        setError('Failed to delete DNS entry: ' + (err.message || 'Unknown error'));
        console.error('Error deleting DNS entry:', err);
      }
    }
  };
  
  const handleToggleDnsStatus = async (entryId, currentStatus) => {
    try {
      const entry = coreDnsEntries.find(e => e.id === entryId);
      if (!entry) {
        throw new Error('DNS entry not found');
      }
      
      const response = await endpointAPI.update(entryId, {
        baseUrl: entry.baseUrl || entry.url,
        name: entry.name
      });
      
      if (response && response.status === 200) {
        console.log('DNS entry status updated successfully');
      } else {
        throw new Error('Failed to update DNS entry status');
      }
      
      await fetchDnsEntries();
    } catch (err) {
      setError('Failed to update DNS entry status: ' + (err.message || 'Unknown error'));
      console.error('Error updating DNS entry status:', err);
    }
  };
  
  // ============ MAINTENANCE MODE FUNCTIONS ============
  
  const fetchMaintenanceStatus = async () => {
    try {
      setMaintenanceMode(prev => ({ ...prev, loading: true }));
      const response = await adminSettingsAPI.getMaintenanceStatus();
      
      if (response && response.data !== undefined) {
        let data = response.data;
        
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (parseError) {
            console.error('Failed to parse maintenance status:', parseError);
            setMaintenanceMode(prev => ({ ...prev, loading: false }));
            return;
          }
        }
        
        let maintenanceData = data;
        if (data.data && typeof data.data === 'object') {
          maintenanceData = data.data;
        }
        
        setMaintenanceMode({
          isEnabled: maintenanceData.isEnabled || false,
          message: maintenanceData.message || '',
          supportEmail: maintenanceData.supportEmail || 'support@wdtp.io',
          allowedRoutes: maintenanceData.allowedRoutes || [],
          startedAt: maintenanceData.startedAt || null,
          endedAt: maintenanceData.endedAt || null,
          loading: false
        });
      } else {
        setMaintenanceMode(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error('Error fetching maintenance status:', err);
      setMaintenanceMode(prev => ({ ...prev, loading: false }));
    }
  };
  
  const handleToggleMaintenanceMode = async () => {
    const newState = !maintenanceMode.isEnabled;
    
    if (newState) {
      const message = editingMaintenance.message || 'System down for updates';
      const supportEmail = editingMaintenance.supportEmail || 'support@wdtp.io';
      
      try {
        setMaintenanceMode(prev => ({ ...prev, loading: true }));
        const response = await adminSettingsAPI.enableMaintenanceMode(message, ['/api/health', '/api/auth/login'], supportEmail);
        
        if (response && (response.status === 200 || response.status === 201)) {
          let responseData = response.data;
          if (responseData && responseData.data && typeof responseData.data === 'object') {
            responseData = responseData.data;
          }
          responseData = responseData || response.data || {};
          
          setMaintenanceMode({
            isEnabled: responseData.isEnabled ?? true,
            message: responseData.message || message,
            supportEmail: responseData.supportEmail || supportEmail,
            allowedRoutes: responseData.allowedRoutes || ['/api/health', '/api/auth/login'],
            startedAt: responseData.startedAt || new Date().toISOString(),
            endedAt: responseData.endedAt || null,
            loading: false
          });
          setEditingMaintenance({
            message: responseData.message || message,
            supportEmail: responseData.supportEmail || supportEmail
          });
          alert('Maintenance mode enabled. All non-admin endpoints are now blocked.');
        } else {
          throw new Error('Failed to enable maintenance mode');
        }
      } catch (err) {
        setError('Failed to enable maintenance mode: ' + (err.message || 'Unknown error'));
        setMaintenanceMode(prev => ({ ...prev, loading: false }));
      }
    } else {
      if (!window.confirm('Disable maintenance mode? This will restore normal operation.')) {
        return;
      }
      
      try {
        setMaintenanceMode(prev => ({ ...prev, loading: true }));
        const response = await adminSettingsAPI.disableMaintenanceMode();
        
        if (response && (response.status === 200 || response.status === 201)) {
          let responseData = response.data;
          if (responseData && responseData.data && typeof responseData.data === 'object') {
            responseData = responseData.data;
          }
          responseData = responseData || response.data || {};
          
          setMaintenanceMode({
            isEnabled: responseData.isEnabled ?? false,
            message: responseData.message || '',
            supportEmail: responseData.supportEmail || maintenanceMode.supportEmail,
            allowedRoutes: responseData.allowedRoutes || [],
            startedAt: responseData.startedAt || null,
            endedAt: responseData.endedAt || new Date().toISOString(),
            loading: false
          });
          setEditingMaintenance({
            message: '',
            supportEmail: 'support@wdtp.io'
          });
          alert('Maintenance mode disabled. System is now operational.');
        } else {
          throw new Error('Failed to disable maintenance mode');
        }
      } catch (err) {
        setError('Failed to disable maintenance mode: ' + (err.message || 'Unknown error'));
        setMaintenanceMode(prev => ({ ...prev, loading: false }));
      }
    }
  };
  
  const handleUpdateMaintenanceSettings = async () => {
    try {
      setMaintenanceMode(prev => ({ ...prev, loading: true }));
      
      const response = await adminSettingsAPI.toggleMaintenanceMode(
        maintenanceMode.isEnabled,
        editingMaintenance.message,
        maintenanceMode.allowedRoutes,
        editingMaintenance.supportEmail
      );
      
      if (response && (response.status === 200 || response.status === 201)) {
        let responseData = response.data;
        if (responseData && responseData.data && typeof responseData.data === 'object') {
          responseData = responseData.data;
        }
        responseData = responseData || response.data || {};
        
        const newMessage = responseData.message || editingMaintenance.message;
        const newSupportEmail = responseData.supportEmail || editingMaintenance.supportEmail;
        
        setMaintenanceMode(prev => ({
          ...prev,
          message: newMessage,
          supportEmail: newSupportEmail,
          loading: false
        }));
        
        setEditingMaintenance({
          message: newMessage,
          supportEmail: newSupportEmail
        });
        
        alert('Maintenance settings updated successfully.');
      } else {
        throw new Error('Failed to update maintenance settings');
      }
    } catch (err) {
      setError('Failed to update maintenance settings: ' + (err.message || 'Unknown error'));
      setMaintenanceMode(prev => ({ ...prev, loading: false }));
    }
  };
  
  const handleEditingChange = (e) => {
    const { name, value } = e.target;
    setEditingMaintenance(prev => ({ ...prev, [name]: value }));
  };
  
  const dismissError = () => {
    setError(null);
  };
  
  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };
  
  // Calculate stats
  const activeCount = coreDnsEntries.filter(e => e.status === 'active').length;
  const inactiveCount = coreDnsEntries.filter(e => e.status === 'inactive').length;
  
  // Render DNS card
  const renderDnsCard = (entry, index) => (
    <div key={entry.id || entry.baseUrl || `dns-${index}`} className="sm-dns-card">
      <div className="sm-dns-header">
        <h4 className="sm-dns-name">
          <i className="fas fa-server"></i>
          {entry.name}
        </h4>
        <span className={`sm-dns-status ${entry.status}`}>
          <i className={`fas ${entry.status === 'active' ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
          {entry.status === 'active' ? 'Active' : 'Inactive'}
        </span>
      </div>
      <div className="sm-dns-info">
        <div className="sm-dns-url">{entry.baseUrl || entry.url}</div>
        <div className="sm-dns-meta">
          <span>Created: {formatDate(entry.createdAt)}</span>
        </div>
        <div className="sm-dns-actions">
          <button 
            className="sm-btn-icon primary" 
            onClick={() => handleOpenEditDnsDialog(entry)}
            title="Edit"
          >
            <i className="fas fa-edit"></i>
          </button>
          <button 
            className={`sm-btn-icon ${entry.status === 'active' ? 'success' : 'primary'}`}
            onClick={() => handleToggleDnsStatus(entry.id, entry.status)}
            title={entry.status === 'active' ? 'Deactivate' : 'Activate'}
          >
            <i className={`fas ${entry.status === 'active' ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
          </button>
          <button 
            className="sm-btn-icon danger" 
            onClick={() => handleDeleteDnsEntry(entry.id)}
            title="Delete"
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
    </div>
  );
  
  // Dialog components
  const AddDnsDialog = ({ show, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
      name: '',
      baseUrl: ''
    });
    
    const handleChange = (e) => {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };
    
    if (!show) return null;
    
    return (
      <div className="sm-modal-overlay" onClick={onClose}>
        <div className="sm-modal" onClick={e => e.stopPropagation()}>
          <div className="sm-modal-header">
            <h3 className="sm-modal-title">
              <i className="fas fa-plus-circle"></i>
              Add Core DNS Entry
            </h3>
            <button type="button" className="sm-modal-close" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="sm-modal-body">
              <div className="sm-modal-info">
                <div className="sm-modal-info-title">
                  <i className="fas fa-info-circle"></i>
                  Core System Services
                </div>
                <p>
                  Core services include authentication, database, cache, and configuration systems. 
                  These DNS entries allow frontend applications to discover and connect to backend services.
                </p>
              </div>
              <div className="sm-form-group">
                <label className="sm-form-label" htmlFor="name">Service Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="sm-form-input"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Auth Service, Database Gateway"
                  required
                />
              </div>
              <div className="sm-form-group">
                <label className="sm-form-label" htmlFor="baseUrl">DNS Address *</label>
                <input
                  type="text"
                  id="baseUrl"
                  name="baseUrl"
                  className="sm-form-input"
                  value={formData.baseUrl}
                  onChange={handleChange}
                  placeholder="auth.example.com"
                  required
                />
                <span className="sm-tip-badge">
                  <i className="fas fa-lightbulb"></i>
                  Without protocol (http:// or https://)
                </span>
              </div>
            </div>
            <div className="sm-modal-footer">
              <button
                type="button"
                className="sm-btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="sm-btn-primary">
                <i className="fas fa-plus"></i>
                Add DNS Entry
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  const EditDnsDialog = ({ show, dnsEntry, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
      name: '',
      baseUrl: ''
    });
    
    useEffect(() => {
      if (dnsEntry) {
        setFormData({
          name: dnsEntry.name || '',
          baseUrl: dnsEntry.baseUrl || dnsEntry.url || ''
        });
      }
    }, [dnsEntry]);
    
    const handleChange = (e) => {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    };
    
    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };
    
    if (!show) return null;
    
    return (
      <div className="sm-modal-overlay" onClick={onClose}>
        <div className="sm-modal" onClick={e => e.stopPropagation()}>
          <div className="sm-modal-header">
            <h3 className="sm-modal-title">
              <i className="fas fa-edit"></i>
              Edit DNS Entry
            </h3>
            <button type="button" className="sm-modal-close" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="sm-modal-body">
              <div className="sm-modal-info">
                <div className="sm-modal-info-title">
                  <i className="fas fa-cog"></i>
                  Update Service
                </div>
                <p>
                  Update the DNS entry details for this core system service.
                </p>
              </div>
              <div className="sm-form-group">
                <label className="sm-form-label" htmlFor="name">Service Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="sm-form-input"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter service name"
                  required
                />
              </div>
              <div className="sm-form-group">
                <label className="sm-form-label" htmlFor="baseUrl">DNS Address *</label>
                <input
                  type="text"
                  id="baseUrl"
                  name="baseUrl"
                  className="sm-form-input"
                  value={formData.baseUrl}
                  onChange={handleChange}
                  placeholder="api.example.com"
                  required
                />
              </div>
            </div>
            <div className="sm-modal-footer">
              <button
                type="button"
                className="sm-btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="sm-btn-primary">
                <i className="fas fa-save"></i>
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };
  
  return (
    <div className="sm-container">
      {/* Header */}
      <div className="sm-header-section">
        <div className="sm-header-content">
          <h2 className="sm-title">
            <i className="fas fa-cogs"></i>
            System Manager
          </h2>
          <p className="sm-description">
            Manage core system initialization services and maintenance mode
          </p>
        </div>
        <div className="sm-header-actions">
          <button className="sm-btn-primary" onClick={fetchDnsEntries}>
            <i className="fas fa-sync-alt"></i>
            Refresh
          </button>
        </div>
      </div>
      
      {/* Error Notice */}
      {error && (
        <div className="sm-error-notice">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
          <button className="close-btn" onClick={dismissError}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
      
      {/* Stats Cards */}
      <div className="sm-stats-container">
        <div className="sm-stat-card">
          <div className="sm-stat-icon dns">
            <i className="fas fa-globe"></i>
          </div>
          <div className="sm-stat-content">
            <h3>{coreDnsEntries.length}</h3>
            <p>Total DNS Entries</p>
          </div>
        </div>
        <div className="sm-stat-card">
          <div className="sm-stat-icon active">
            <i className="fas fa-check"></i>
          </div>
          <div className="sm-stat-content">
            <h3>{activeCount}</h3>
            <p>Active Services</p>
          </div>
        </div>
        <div className="sm-stat-card">
          <div className="sm-stat-icon inactive">
            <i className="fas fa-times"></i>
          </div>
          <div className="sm-stat-content">
            <h3>{inactiveCount}</h3>
            <p>Inactive Services</p>
          </div>
        </div>
        <div className="sm-stat-card">
          <div className={`sm-stat-icon ${maintenanceMode.isEnabled ? 'maintenance' : 'active'}`}>
            <i className={`fas ${maintenanceMode.isEnabled ? 'fa-tools' : 'fa-shield-alt'}`}></i>
          </div>
          <div className="sm-stat-content">
            <h3>{maintenanceMode.isEnabled ? 'On' : 'Off'}</h3>
            <p>Maintenance Mode</p>
          </div>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div className="sm-tab-navigation">
        <button 
          className={`sm-tab-button ${activeTab === 'dns' ? 'active' : ''}`}
          onClick={() => setActiveTab('dns')}
        >
          <i className="fas fa-globe"></i>
          Core DNS Services
        </button>
        <button 
          className={`sm-tab-button ${activeTab === 'maintenance' ? 'active' : ''}`}
          onClick={() => setActiveTab('maintenance')}
        >
          <i className="fas fa-tools"></i>
          Maintenance Mode
        </button>
      </div>
      
      {/* Tab Content */}
      <div className="sm-tab-content">
        {/* DNS Tab */}
        {activeTab === 'dns' && (
          <div className="sm-content-panel">
            <div className="sm-panel-header">
              <h3 className="sm-panel-title">
                <i className="fas fa-server"></i>
                Core System DNS Management
              </h3>
              <button 
                className="sm-btn-primary"
                onClick={handleOpenAddDnsDialog}
              >
                <i className="fas fa-plus"></i>
                Add DNS Entry
              </button>
            </div>
            
            {loading ? (
              <div className="sm-loading-container">
                <div className="sm-loading-spinner"></div>
                <p className="sm-loading-text">Loading DNS entries...</p>
              </div>
            ) : coreDnsEntries.length === 0 ? (
              <div className="sm-no-data">
                <i className="fas fa-globe"></i>
                <h4>No DNS Entries Found</h4>
                <p>Add your first core system DNS entry to get started.</p>
                <button className="sm-btn-primary" onClick={handleOpenAddDnsDialog}>
                  <i className="fas fa-plus"></i>
                  Add DNS Entry
                </button>
              </div>
            ) : (
              <div className="sm-dns-grid">
                {coreDnsEntries.map(renderDnsCard)}
              </div>
            )}
          </div>
        )}
        
        {/* Maintenance Tab */}
        {activeTab === 'maintenance' && (
          <div className="sm-maintenance-panel">
            <div className="sm-maintenance-header">
              <div className="sm-maintenance-status">
                <div className="sm-maintenance-info">
                  <div className={`sm-maintenance-icon ${maintenanceMode.isEnabled ? 'active' : 'inactive'}`}>
                    <i className={`fas ${maintenanceMode.isEnabled ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
                  </div>
                  <div className="sm-maintenance-text">
                    <h4>
                      {maintenanceMode.isEnabled 
                        ? 'System is in Maintenance Mode' 
                        : 'System is Operational'}
                    </h4>
                    {maintenanceMode.isEnabled && maintenanceMode.message && (
                      <p>Message: "{maintenanceMode.message}"</p>
                    )}
                  </div>
                </div>
                {adminHasPermission('settings.maintenance') ? (
                  <button 
                    className={`sm-maintenance-toggle ${maintenanceMode.isEnabled ? 'disable' : 'enable'}`}
                    onClick={handleToggleMaintenanceMode}
                    disabled={maintenanceMode.loading}
                  >
                    {maintenanceMode.loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Processing...
                      </>
                    ) : maintenanceMode.isEnabled ? (
                      <>
                        <i className="fas fa-times"></i>
                        Disable Maintenance
                      </>
                    ) : (
                      <>
                        <i className="fas fa-power-off"></i>
                        Enable Maintenance
                      </>
                    )}
                  </button>
                ) : (
                  <div className="sm-permission-notice" style={{ padding: '8px 16px', background: 'rgba(255,193,7,0.1)', borderRadius: '6px', fontSize: '13px', color: '#ffc107' }}>
                    <i className="fas fa-lock"></i> Super Admin only
                  </div>
                )}
              </div>
            </div>
            
            {adminHasPermission('settings.maintenance') ? (
              <div className="sm-maintenance-settings">
                <h4 className="sm-settings-title">
                  <i className="fas fa-cog"></i>
                  Maintenance Settings
                </h4>
                <div className="sm-settings-grid">
                  <div className="sm-form-group">
                    <label className="sm-form-label" htmlFor="message">Maintenance Message</label>
                    <input
                      type="text"
                      id="message"
                      name="message"
                      className="sm-form-input"
                      value={editingMaintenance.message}
                      onChange={handleEditingChange}
                      placeholder="Enter maintenance message..."
                    />
                  </div>
                  <div className="sm-form-group">
                    <label className="sm-form-label" htmlFor="supportEmail">Support Email</label>
                    <input
                      type="email"
                      id="supportEmail"
                      name="supportEmail"
                      className="sm-form-input"
                      value={editingMaintenance.supportEmail}
                      onChange={handleEditingChange}
                      placeholder="support@example.com"
                    />
                  </div>
                </div>
                <button
                  className="sm-btn-primary"
                  onClick={handleUpdateMaintenanceSettings}
                  disabled={maintenanceMode.loading}
                >
                  <i className="fas fa-save"></i>
                  Save Settings
                </button>
              </div>
            ) : (
              <div className="sm-maintenance-settings">
                <div className="sm-info-title" style={{ color: '#6c757d', fontStyle: 'italic' }}>
                  <i className="fas fa-info-circle"></i> Maintenance settings can only be modified by a Super Admin.
                </div>
              </div>
            )}
            
            <div className="sm-maintenance-info-section">
              <h4 className="sm-info-title">
                <i className="fas fa-info-circle"></i>
                {maintenanceMode.isEnabled ? 'Allowed Routes During Maintenance:' : 'Maintenance Mode Information:'}
              </h4>
              {maintenanceMode.isEnabled && maintenanceMode.allowedRoutes && maintenanceMode.allowedRoutes.length > 0 ? (
                <ul className="sm-route-list">
                  {maintenanceMode.allowedRoutes.map((route, idx) => (
                    <li key={idx} className="sm-route-item">{route}</li>
                  ))}
                </ul>
              ) : (
                <ul className="sm-route-list">
                  <li className="sm-route-item">Admin routes and /api/health remain accessible</li>
                  <li className="sm-route-item">All other endpoints return maintenance error</li>
                  <li className="sm-route-item">Use this mode during system updates</li>
                </ul>
              )}
              {maintenanceMode.isEnabled && maintenanceMode.supportEmail && (
                <div className="sm-support-info">
                  <strong>Support:</strong>{' '}
                  <a href={`mailto:${maintenanceMode.supportEmail}`} className="sm-support-link">
                    {maintenanceMode.supportEmail}
                  </a>
                </div>
              )}
              {maintenanceMode.isEnabled && (maintenanceMode.startedAt || maintenanceMode.endedAt) && (
                <div className="sm-timestamps">
                  {maintenanceMode.startedAt && (
                    <div className="sm-timestamp-item">
                      <div className="sm-timestamp-label">Started At</div>
                      <div className="sm-timestamp-value">{new Date(maintenanceMode.startedAt).toLocaleString()}</div>
                    </div>
                  )}
                  {maintenanceMode.endedAt && (
                    <div className="sm-timestamp-item">
                      <div className="sm-timestamp-label">Ended At</div>
                      <div className="sm-timestamp-value">{new Date(maintenanceMode.endedAt).toLocaleString()}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Dialogs */}
      <AddDnsDialog 
        show={showAddDnsDialog}
        onClose={handleCloseAddDnsDialog}
        onSubmit={handleAddDnsEntry}
      />
      
      <EditDnsDialog 
        show={showEditDnsDialog}
        dnsEntry={currentDnsEntry}
        onClose={handleCloseEditDnsDialog}
        onSubmit={handleEditDnsEntry}
      />
    </div>
  );
};

export default SystemManager;
